import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Wrench, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { usersApi } from '@/api/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

// React.FormEvent needed for plain form submit handler
import type React from 'react';

const loginSchema = z.object({
  username: z.string().email('Please enter a valid email').min(1, 'Email is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const { isAuthenticated, newPasswordRequired, pendingEmail, signIn, confirmNewPassword } = useAuth();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [newPwError, setNewPwError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  const from = (location.state as { from?: Location })?.from?.pathname ?? '/dashboard';

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(values: LoginFormData) {
    setAuthError(null);
    try {
      await signIn(values.username, values.password);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sign in failed. Please check your credentials.';
      setAuthError(msg);
    }
  }

  async function onNewPasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setNewPwError('');
    setAuthError(null);
    if (newPw.length < 8) { setNewPwError('Password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { setNewPwError('Passwords do not match'); return; }
    if (!termsAccepted) { setNewPwError('You must accept the Terms of Service to continue'); return; }
    setIsSettingPassword(true);
    try {
      await confirmNewPassword(newPw);
      // Fire-and-forget — don't block login if consent record fails
      void usersApi.recordConsent({ termsAcceptedAt: new Date().toISOString(), smsConsent }).catch(() => null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to set new password.';
      setAuthError(msg);
    } finally {
      setIsSettingPassword(false);
    }
  }

  // ── New password required step ─────────────────────────────────────────────
  if (newPasswordRequired) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
        <div className="w-full max-w-md">
          <div className="mb-8 flex flex-col items-center gap-2">
            <div className="flex items-center justify-center rounded-full bg-primary p-3">
              <Wrench className="h-8 w-8 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">RepairApp</h1>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-center text-xl">Set Your Password</CardTitle>
              <CardDescription className="text-center">
                This is your first login. Please create a permanent password.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onNewPasswordSubmit} className="space-y-4">
                {/* Hidden username — Chrome requires this for password fields to be interactive */}
                <input type="text" autoComplete="username" defaultValue={pendingEmail} className="hidden" />

                <div className="space-y-1.5">
                  <label htmlFor="new-pw" className="text-sm font-medium">New Password</label>
                  <div className="relative">
                    <Input
                      id="new-pw"
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      className="pr-10"
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                    />
                    <button type="button" tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowNewPassword((v) => !v)}
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}>
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="confirm-pw" className="text-sm font-medium">Confirm Password</label>
                  <div className="relative">
                    <Input
                      id="confirm-pw"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      className="pr-10"
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                    />
                    <button type="button" tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="rounded-md border border-border p-3 space-y-3 text-sm">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <span>
                      I agree to the{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline text-primary">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-primary">
                        Privacy Policy
                      </a>
                      {' '}<span className="text-destructive">*</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                    />
                    <span className="text-muted-foreground">
                      I agree to receive SMS notifications about repair orders and maintenance alerts.
                      Message &amp; data rates may apply. Reply STOP to unsubscribe. (Optional)
                    </span>
                  </label>
                </div>

                {newPwError && (
                  <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{newPwError}</p>
                )}
                {authError && (
                  <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{authError}</p>
                )}

                <Button type="submit" className="w-full" disabled={isSettingPassword || !termsAccepted}>
                  {isSettingPassword ? 'Saving…' : 'Set Password & Sign In'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Normal login ───────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center gap-2">
          <div className="flex items-center justify-center rounded-full bg-primary p-3">
            <Wrench className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">RepairApp</h1>
          <p className="text-sm text-muted-foreground">
            Fleet repair & maintenance management
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-xl">Sign in to your account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
                noValidate
              >
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="you@company.com"
                          autoComplete="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            autoComplete="current-password"
                            className="pr-10"
                            {...field}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setShowPassword((v) => !v)}
                            tabIndex={-1}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {authError && (
                  <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {authError}
                  </p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? 'Signing in…' : 'Sign In'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Powered by AWS Cognito authentication
        </p>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>
          {' · '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a>
        </p>
      </div>
    </div>
  );
}
